<template>
  <EPill
    class="notification-pill"
    :class="{ highlight }"
    :active="highlight"
    v-on="$listeners"
    @click="onClick"
  >
    <BellIcon class="notification-pill__icon" />
    <span v-if="highlight" class="notification-pill__alert" />
    <span class="sr-only" data-test-id="sr-only">Toggle notifications</span>
  </EPill>
</template>

<script>
import { mapState } from 'vuex';

import { LAST_READ_NOTIFICATION as LAST_READ_NOTIFICATION_COOKIE } from '~~/common/js/constants/cookies';
import { getCookieOptions, isDateInLastDays } from '~~/common/js/universal/utils';
import { sendEvent } from '~~/common/js/client/analytics';
import EPill from '~~/common/vue/components/pill';
import BellIcon from '~~/common/vue/components/icons/m/bell.svg';

import {
  MIN_TREE_COUNT_PILL,
  NOTIFICATION_DAYS_PILL,
  NOTIFICATION_LEVEL_PILL,
} from './constants';

export default {
  name: 'ENotificationPill',
  components: {
    BellIcon,
    EPill,
  },
  data() {
    return {
      dismissed: false,
      impressionEventSent: false,
    };
  },
  computed: {
    ...mapState('user', [
      'lastReadNotificationTimestamp',
      'notificationEnabled',
      'treeCount',
    ]),
    notification() {
      return this.$store.getters['notifications/getForLevel'](NOTIFICATION_LEVEL_PILL);
    },
    highlight() {
      if (this.dismissed || !this.notification) {
        return false;
      }
      const minTreeCount = this.treeCount >= MIN_TREE_COUNT_PILL;
      const publishDate = new Date(this.notification.publish_date).getTime();
      const publishedRecently = isDateInLastDays(publishDate, NOTIFICATION_DAYS_PILL);
      const neverSeen = (
        !this.lastReadNotificationTimestamp ||
        (
          this.lastReadNotificationTimestamp &&
          new Date(this.lastReadNotificationTimestamp) < publishDate
        )
      );

      return (
        this.notificationEnabled &&
        minTreeCount &&
        publishedRecently &&
        neverSeen
      );
    },
  },
  mounted() {
    this.unwatch = this.$watch('highlight', this.sendImpressionEvent.bind(this), { immediate: true });
  },
  destroy() {
    if (this.unwatch) {
      this.unwatch();
    }
  },
  methods: {
    sendEvent(name) {
      sendEvent(name, { origin: 'center' });
    },
    sendImpressionEvent(highlight) {
      if (highlight && !this.impressionEventSent) {
        this.sendEvent('all_notification_impression');
        this.impressionEventSent = true;
      }
    },
    storeLastRead() {
      const key = LAST_READ_NOTIFICATION_COOKIE;
      const value = Date.now();
      const options = getCookieOptions();
      this.$cookies.set(key, value, options);
    },
    onClick() {
      this.dismissed = true;
      this.storeLastRead();
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-pill {
  &:hover path {
    fill: $gray-70;
  }

  &:focus {
    background-color: $gray-20;

    path {
      fill: $gray-70;
    }
  }
}
</style>

<style lang="scss">
.notification-pill__icon {
  color: $color-text-secondary;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
}

.notification-pill.highlight .notification-pill__alert {
  height: $space-s;
  width: $space-s;
  background-color: $color-brand-tertiary;
  border-radius: $border-radius-full;
  position: absolute;
  top: 0;
  right: 0;
}
</style>
